// src/services/adminNotificationService.js - Admin push notification service
import { collection, query, where, getDocs, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { db, appId } from '../firebase/firebaseConfig.jsx';
import { offlineMessageQueue } from '../utils/OfflineMessageQueue';

class AdminNotificationService {
  constructor() {
    this.isInitialized = false;
    this.apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
  }

  /**
   * Admin 푸시알림 서비스 초기화
   */
  async initialize() {
    if (this.isInitialized) {
      return true;
    }

    try {
      console.log('🔔 AdminNotificationService 초기화 중...');
      this.isInitialized = true;
      console.log('✅ AdminNotificationService 초기화 완료');
      return true;
    } catch (error) {
      console.error('❌ AdminNotificationService 초기화 실패:', error);
      return false;
    }
  }

  /**
   * 모든 사용자의 FCM 토큰 조회
   * @returns {Array} 사용자 FCM 토큰 배열
   */
  async getAllUserFcmTokens() {
    try {
      console.log('🔍 모든 사용자의 FCM 토큰 조회 중...');
      
      // 사용자관리와 동일한 다중 경로 검색 전략 적용
      const hardcodedAppId = 'kairos-platform-1ffde';
      const allUsers = [];
      const processedUserIds = new Set(); // 중복 방지
      
      // 유효한 경로만 (Firestore 규칙: 홀수 개 세그먼트, 권한 있는 경로만)
      const userCollectionPaths = [
        `artifacts/${hardcodedAppId}/users`,  // 메인 경로 ✅
        'users'                               // 직접 경로 ✅
      ];
      
      // 각 경로에서 사용자 정보 수집
      for (const collectionPath of userCollectionPaths) {
        try {
          console.log(`📍 경로 검색: ${collectionPath}`);
          const usersRef = collection(db, collectionPath);
          const usersSnapshot = await getDocs(usersRef);
          console.log(`📊 ${collectionPath} 문서 수:`, usersSnapshot.docs.length);
          
          for (const userDoc of usersSnapshot.docs) {
            const userData = userDoc.data();
            const userId = userDoc.id;
            
            // 이미 처리된 사용자인지 확인
            if (processedUserIds.has(userId)) {
              continue;
            }
            
            console.log(`👤 사용자 발견: ${userId} (${collectionPath})`);
            
            // 기본 사용자 정보 구성
            const userInfo = {
              id: userId,
              ...userData,
            };
            
            allUsers.push(userInfo);
            processedUserIds.add(userId);
            console.log(`✅ 사용자 추가 완료: ${userInfo.email || userId}`);
          }
        } catch (error) {
          console.warn(`❌ ${collectionPath} 로드 실패:`, error.message);
          // 개별 경로 실패는 전체를 중단시키지 않음
        }
      }
      
      console.log('✅ 최종 사용자 수집 결과:');
      console.log(`📊 총 발견된 사용자: ${allUsers.length}명`);
      
      // 기존 로직을 allUsers 배열로 변경
      const usersSnapshot = { docs: allUsers.map(user => ({ id: user.id, data: () => user })) };
      
      console.log(`👥 총 사용자 수: ${usersSnapshot.docs.length}`);
      
      // 사용자 목록 상세 로깅
      console.log('📋 사용자 목록:');
      usersSnapshot.docs.forEach((userDoc, index) => {
        const userData = userDoc.data();
        console.log(`  ${index + 1}. ${userDoc.id} - email: ${userData.email || 'N/A'}, role: ${userData.role || 'N/A'}, isAdmin: ${userData.isAdmin || false}`);
      });
      
      const allTokens = [];
      let userCount = 0;
      
      for (const userDoc of usersSnapshot.docs) {
        const userId = userDoc.id;
        const userData = userDoc.data();
        userCount++;
        
        // Admin 사용자 제외 로직 (더 정확하게)
        const isAdmin = userData.role === 'admin' || 
                       userData.isAdmin === true ||
                       (userData.email && (
                         userData.email.toLowerCase().includes('@admin.') ||
                         userData.email.toLowerCase().endsWith('@admin.com') ||
                         userData.email.toLowerCase().includes('admin@')
                       ));
        
        if (isAdmin) {
          console.log(`🚫 Admin 사용자 제외: ${userId} (${userData.email || 'N/A'}) - role: ${userData.role}, isAdmin: ${userData.isAdmin}`);
          continue;
        }
        
        // Query each user's deviceTokens collection
        const deviceTokensRef = collection(db, `artifacts/${hardcodedAppId}/users/${userId}/deviceTokens`);
        const tokensSnapshot = await getDocs(deviceTokensRef);
        
        console.log(`👤 사용자 ${userCount}/${usersSnapshot.docs.length} (${userId}): ${tokensSnapshot.docs.length}개 토큰`);
        
        // ✅ 네이티브 토큰 우선 수집 (Web 토큰은 네이티브 토큰이 없을 때만 사용)
        const userNativeTokens = [];
        const userWebTokens = [];
        
        tokensSnapshot.docs.forEach(tokenDoc => {
          const tokenData = tokenDoc.data();
          // 🔥 isActive 필터링 추가 (비활성 토큰 제외)
          const isActive = tokenData.isActive !== false;
          
          if (tokenData.token && this.isValidToken(tokenData.token) && isActive) {
            const platform = tokenData.platform || 'web';
            const tokenType = tokenData.tokenType || '';
            const isNativeToken = platform === 'android' || 
                                  platform === 'ios' || 
                                  tokenType === 'native' ||
                                  platform === 'native';
            const isWebToken = platform === 'web' || (!platform && !isNativeToken);
            
            const tokenInfo = {
              userId: userId,
              token: tokenData.token,
              platform: platform,
              tokenId: tokenDoc.id,
              locale: tokenData.locale || 'en'
            };
            
            if (isNativeToken) {
              userNativeTokens.push(tokenInfo);
              console.log(`  ✅ 네이티브 토큰: ${tokenData.token.substring(0, 20)}... (platform: ${platform}, active: ${isActive})`);
            } else if (isWebToken) {
              userWebTokens.push(tokenInfo);
              console.log(`  ⚠️ Web 토큰: ${tokenData.token.substring(0, 20)}... (네이티브 토큰 우선)`);
            }
          } else if (!isActive) {
            console.log(`  ⏭️ 비활성 토큰 제외: ${tokenData.token?.substring(0, 20)}...`);
          }
        });
        
        // ✅ 네이티브 토큰이 있으면 네이티브 토큰만 사용, 없으면 Web 토큰 사용
        if (userNativeTokens.length > 0) {
          allTokens.push(...userNativeTokens);
          console.log(`  ✅ 사용자 ${userId}: 네이티브 토큰 ${userNativeTokens.length}개 사용 (Web 토큰 ${userWebTokens.length}개 제외)`);
        } else {
          allTokens.push(...userWebTokens);
          console.log(`  ⚠️ 사용자 ${userId}: 네이티브 토큰 없음 - Web 토큰 ${userWebTokens.length}개 사용`);
        }
        
        // ✅ 로그아웃 상태 토큰(emergency_tokens)도 조회
        try {
          const emergencyTokensRef = collection(db, 'emergency_tokens');
          const emergencyTokensSnapshot = await getDocs(
            query(emergencyTokensRef, where('lastUserId', '==', userId), where('isActive', '==', true))
          );
          
          emergencyTokensSnapshot.docs.forEach(emergencyDoc => {
            const emergencyData = emergencyDoc.data();
            if (emergencyData.token && this.isValidToken(emergencyData.token)) {
              // ✅ 중복 방지: 이미 allTokens에 없는 경우만 추가
              const existingToken = allTokens.find(t => t.token === emergencyData.token);
              if (!existingToken) {
                const platform = emergencyData.platform || 'web';
                const isNativeToken = platform === 'android' || 
                                      platform === 'ios' || 
                                      platform === 'native';
                
                // ✅ 네이티브 토큰만 추가 (Web 토큰은 제외)
                if (isNativeToken) {
                  allTokens.push({
                    userId: userId,
                    token: emergencyData.token,
                    platform: platform,
                    tokenId: emergencyDoc.id,
                    locale: emergencyData.locale || 'en',
                    source: 'emergency_tokens'
                  });
                  console.log(`  ✅ [emergency_tokens] 네이티브 토큰 추가: ${emergencyData.token.substring(0, 20)}...`);
                }
              }
            }
          });
        } catch (emergencyError) {
          console.warn(`⚠️ emergency_tokens 조회 실패 (무시 가능): ${emergencyError.message}`);
        }
      }
      
      console.log(`🔍 총 ${allTokens.length}개의 유효한 FCM 토큰을 찾았습니다.`);
      console.log('📊 토큰 상세 정보:', allTokens.map(t => ({
        userId: t.userId,
        tokenLength: t.token.length,
        platform: t.platform,
        locale: t.locale
      })));
      return allTokens;
      
    } catch (error) {
      console.error('❌ FCM 토큰 조회 실패:', error);
      return [];
    }
  }

  /**
   * FCM 토큰 유효성 검증
   */
  isValidToken(token) {
    return token && 
           typeof token === 'string' && 
           token.length > 10 && // Mock 토큰도 허용하도록 길이 조건 완화
           (token.includes(':') || token.startsWith('mock_fcm_token')); // Mock 토큰도 유효한 것으로 처리
  }

  /**
   * 비활성 사용자 FCM 토큰 조회 (30일 이상 송금 활동 없음)
   * @returns {Array} 비활성 사용자 FCM 토큰 배열
   */
  async getInactiveUserFcmTokens() {
    try {
      console.log('🔍 비활성 사용자의 FCM 토큰 조회 중...');
      
      const hardcodedAppId = 'kairos-platform-1ffde';
      const usersRef = collection(db, `artifacts/${hardcodedAppId}/users`);
      const usersSnapshot = await getDocs(usersRef);
      
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const inactiveTokens = [];
      
      for (const userDoc of usersSnapshot.docs) {
        const userId = userDoc.id;
        const userData = userDoc.data();
        
        // Admin 사용자 제외 로직 (더 정확하게)
        const isAdmin = userData.role === 'admin' || 
                       userData.isAdmin === true ||
                       (userData.email && (
                         userData.email.toLowerCase().includes('@admin.') ||
                         userData.email.toLowerCase().endsWith('@admin.com') ||
                         userData.email.toLowerCase().includes('admin@')
                       ));
        
        if (isAdmin) {
          console.log(`🚫 Admin 사용자 제외 (비활성): ${userId} (${userData.email || 'N/A'}) - role: ${userData.role}, isAdmin: ${userData.isAdmin}`);
          continue;
        }
        
        // Check last activity date
        const lastActivity = userData.lastActivity?.toDate?.() || userData.createdAt?.toDate?.() || new Date();
        
        if (lastActivity < thirtyDaysAgo) {
          // Query FCM tokens for inactive users
          const deviceTokensRef = collection(db, `artifacts/${hardcodedAppId}/users/${userId}/deviceTokens`);
          const tokensSnapshot = await getDocs(deviceTokensRef);
          
          // ✅ 네이티브 토큰 우선 수집 (Web 토큰은 네이티브 토큰이 없을 때만 사용)
          const userNativeTokens = [];
          const userWebTokens = [];
          
          tokensSnapshot.docs.forEach(tokenDoc => {
            const tokenData = tokenDoc.data();
            // 🔥 isActive 필터링 추가 (비활성 토큰 제외)
            const isActive = tokenData.isActive !== false;
            
            if (tokenData.token && this.isValidToken(tokenData.token) && isActive) {
              const platform = tokenData.platform || 'web';
              const tokenType = tokenData.tokenType || '';
              const isNativeToken = platform === 'android' || 
                                    platform === 'ios' || 
                                    tokenType === 'native' ||
                                    platform === 'native';
              const isWebToken = platform === 'web' || (!platform && !isNativeToken);
              
              const tokenInfo = {
                userId: userId,
                token: tokenData.token,
                platform: platform,
                tokenId: tokenDoc.id,
                locale: tokenData.locale || 'en',
                lastActivity: lastActivity,
                inactiveDays: Math.floor((new Date() - lastActivity) / (1000 * 60 * 60 * 24))
              };
              
              if (isNativeToken) {
                userNativeTokens.push(tokenInfo);
              } else if (isWebToken) {
                userWebTokens.push(tokenInfo);
              }
            }
          });
          
          // ✅ 네이티브 토큰이 있으면 네이티브 토큰만 사용, 없으면 Web 토큰 사용
          if (userNativeTokens.length > 0) {
            inactiveTokens.push(...userNativeTokens);
            console.log(`  ✅ 비활성 사용자 ${userId}: 네이티브 토큰 ${userNativeTokens.length}개 사용 (Web 토큰 ${userWebTokens.length}개 제외)`);
          } else {
            inactiveTokens.push(...userWebTokens);
            console.log(`  ⚠️ 비활성 사용자 ${userId}: 네이티브 토큰 없음 - Web 토큰 ${userWebTokens.length}개 사용`);
          }
        }
      }
      
      console.log(`🔍 총 ${inactiveTokens.length}개의 비활성 사용자 FCM 토큰을 찾았습니다.`);
      return inactiveTokens;
      
    } catch (error) {
      console.error('❌ 비활성 사용자 FCM 토큰 조회 실패:', error);
      return [];
    }
  }

  /**
   * 특정 사용자들의 FCM 토큰 조회
   * @param {Array} userIds - 사용자 ID 배열
   * @returns {Array} 특정 사용자 FCM 토큰 배열
   */
  async getSpecificUserFcmTokens(userIds) {
    try {
      console.log('🔍 특정 사용자들의 FCM 토큰 조회 중...');
      console.log('📋 입력된 사용자 ID 목록:', userIds);
      console.log('📊 사용자 ID 배열 길이:', userIds?.length || 0);
      
      if (!userIds || !Array.isArray(userIds) || userIds.length === 0) {
        console.warn('⚠️ 특정 사용자 ID 배열이 비어있거나 유효하지 않습니다.');
        return [];
      }
      
      const specificTokens = [];
      
      for (const userId of userIds) {
        // Admin 사용자 확인을 위해 사용자 데이터 조회
        const userRef = doc(db, `artifacts/${appId}/users/${userId}`);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          
          // Admin 사용자 제외 로직 (더 정확하게)
          const isAdmin = userData.role === 'admin' || 
                         userData.isAdmin === true ||
                         (userData.email && (
                           userData.email.toLowerCase().includes('@admin.') ||
                           userData.email.toLowerCase().endsWith('@admin.com') ||
                           userData.email.toLowerCase().includes('admin@')
                         ));
          
          if (isAdmin) {
            console.log(`🚫 Admin 사용자 제외 (특정): ${userId} (${userData.email || 'N/A'}) - role: ${userData.role}, isAdmin: ${userData.isAdmin}`);
            continue;
          }
        }
        
        const hardcodedAppId = 'kairos-platform-1ffde';
        const deviceTokensRef = collection(db, `artifacts/${hardcodedAppId}/users/${userId}/deviceTokens`);
        const tokensSnapshot = await getDocs(deviceTokensRef);
        
        // ✅ 네이티브 토큰 우선 수집 (Web 토큰은 네이티브 토큰이 없을 때만 사용)
        const userNativeTokens = [];
        const userWebTokens = [];
        
        tokensSnapshot.docs.forEach(tokenDoc => {
          const tokenData = tokenDoc.data();
          // 🔥 isActive 필터링 추가 (비활성 토큰 제외)
          const isActive = tokenData.isActive !== false;
          
          if (tokenData.token && this.isValidToken(tokenData.token) && isActive) {
            const platform = tokenData.platform || 'web';
            const tokenType = tokenData.tokenType || '';
            const isNativeToken = platform === 'android' || 
                                  platform === 'ios' || 
                                  tokenType === 'native' ||
                                  platform === 'native';
            const isWebToken = platform === 'web' || (!platform && !isNativeToken);
            
            const tokenInfo = {
              userId: userId,
              token: tokenData.token,
              platform: platform,
              tokenId: tokenDoc.id,
              locale: tokenData.locale || 'en'
            };
            
            if (isNativeToken) {
              userNativeTokens.push(tokenInfo);
              console.log(`  ✅ 네이티브 토큰: ${tokenData.token.substring(0, 20)}... (platform: ${platform}, active: ${isActive})`);
            } else if (isWebToken) {
              userWebTokens.push(tokenInfo);
              console.log(`  ⚠️ Web 토큰: ${tokenData.token.substring(0, 20)}... (네이티브 토큰 우선)`);
            }
          } else if (!isActive) {
            console.log(`  ⏭️ 비활성 토큰 제외: ${tokenData.token?.substring(0, 20)}...`);
          }
        });
        
        // ✅ 네이티브 토큰이 있으면 네이티브 토큰만 사용, 없으면 Web 토큰 사용
        if (userNativeTokens.length > 0) {
          specificTokens.push(...userNativeTokens);
          console.log(`  ✅ 사용자 ${userId}: 네이티브 토큰 ${userNativeTokens.length}개 사용 (Web 토큰 ${userWebTokens.length}개 제외)`);
        } else {
          specificTokens.push(...userWebTokens);
          console.log(`  ⚠️ 사용자 ${userId}: 네이티브 토큰 없음 - Web 토큰 ${userWebTokens.length}개 사용`);
        }
        
        // ✅ 로그아웃 상태 토큰(emergency_tokens)도 조회
        try {
          const emergencyTokensRef = collection(db, 'emergency_tokens');
          const emergencyTokensSnapshot = await getDocs(
            query(emergencyTokensRef, where('lastUserId', '==', userId), where('isActive', '==', true))
          );
          
          emergencyTokensSnapshot.docs.forEach(emergencyDoc => {
            const emergencyData = emergencyDoc.data();
            if (emergencyData.token && this.isValidToken(emergencyData.token)) {
              // ✅ 중복 방지: 이미 specificTokens에 없는 경우만 추가
              const existingToken = specificTokens.find(t => t.token === emergencyData.token);
              if (!existingToken) {
                const platform = emergencyData.platform || 'web';
                const isNativeToken = platform === 'android' || 
                                      platform === 'ios' || 
                                      platform === 'native';
                
                // ✅ 네이티브 토큰만 추가 (Web 토큰은 제외)
                if (isNativeToken) {
                  specificTokens.push({
                    userId: userId,
                    token: emergencyData.token,
                    platform: platform,
                    tokenId: emergencyDoc.id,
                    locale: emergencyData.locale || 'en',
                    source: 'emergency_tokens'
                  });
                  console.log(`  ✅ [emergency_tokens] 네이티브 토큰 추가: ${emergencyData.token.substring(0, 20)}...`);
                }
              }
            }
          });
        } catch (emergencyError) {
          console.warn(`⚠️ emergency_tokens 조회 실패 (무시 가능): ${emergencyError.message}`);
        }
      }
      
      console.log(`🔍 총 ${specificTokens.length}개의 특정 사용자 FCM 토큰을 찾았습니다.`);
      return specificTokens;
      
    } catch (error) {
      console.error('❌ 특정 사용자 FCM 토큰 조회 실패:', error);
      return [];
    }
  }

  /**
   * 일반 푸시알림 발송 (통합)
   * @param {Object} notificationData - 알림 데이터
   */
  async sendNotification(notificationData) {
    try {
      console.log('🔔 AdminNotificationService.sendNotification 호출됨:', notificationData);
      
      if (!this.isInitialized) {
        console.log('🔧 서비스 초기화 중...');
        await this.initialize();
      }

      console.log('🔔 푸시알림 발송 시작:', notificationData.title);
      console.log('🎯 타겟 설정:', {
        targetType: notificationData.targetType,
        targetUsers: notificationData.targetUsers,
        targetUsersLength: notificationData.targetUsers?.length || 0
      });
      console.log('🔍 targetType 상세:', {
        value: notificationData.targetType,
        type: typeof notificationData.targetType,
        isAll: notificationData.targetType === 'all',
        isAllUsers: notificationData.targetType === 'all_users'
      });

      // Determine target users
      let targetUsers = [];
      if (notificationData.targetType === 'all' || notificationData.targetType === 'all_users') {
        console.log('📢 전체 사용자 타겟팅 시작');
        targetUsers = await this.getAllUserFcmTokens();
      } else if (notificationData.targetType === 'inactive_users') {
        console.log('😴 비활성 사용자 타겟팅 시작');
        targetUsers = await this.getInactiveUserFcmTokens();
      } else if (notificationData.targetType === 'specific_users') {
        console.log('🎯 특정 사용자 타겟팅 시작:', notificationData.targetUsers);
        console.log('🔍 targetUsers 타입:', typeof notificationData.targetUsers);
        console.log('🔍 targetUsers 길이:', notificationData.targetUsers?.length);
        console.log('🔍 targetUsers 내용:', JSON.stringify(notificationData.targetUsers, null, 2));
        
        if (!notificationData.targetUsers || notificationData.targetUsers.length === 0) {
          console.warn('⚠️ specific_users 타겟팅이지만 targetUsers가 비어있습니다. 전체 사용자로 대체합니다.');
          targetUsers = await this.getAllUserFcmTokens();
        } else {
          targetUsers = await this.getSpecificUserFcmTokens(notificationData.targetUsers);
        }
      }

      if (targetUsers.length === 0) {
        console.warn('⚠️ 발송할 활성 토큰이 없습니다.');
        console.log('🔍 토큰 조회 결과:');
        console.log('- 전체 사용자:', notificationData.targetType);
        console.log('- 조회된 토큰 수:', targetUsers.length);
        return { success: false, reason: 'no_tokens', sentCount: 0 };
      }
      
      console.log(`✅ 발송 대상 토큰 수: ${targetUsers.length}개`);

      // Set sender information
      const senderInfo = this.getSenderInfo(notificationData.sender);
      
      // Group tokens by language
      const tokensByLocale = this.groupTokensByLocale(targetUsers);
      
      let totalSuccess = 0;
      let totalFailure = 0;
      const results = [];

      // Send notifications for each language
      for (const [locale, tokens] of tokensByLocale.entries()) {
        console.log(`📱 ${locale} 언어로 ${tokens.length}명에게 알림 발송`);
        
        const notification = this.createNotification(notificationData, senderInfo, locale);
        
        // Direct Backend API call
        // 🔥 토큰 정보 객체 전체를 전송 (백엔드에서 platform, userId 등 추출 가능하도록)
        // 🔥 백그라운드 알림 수신을 위해 data 필드에 title, body 포함
        const enhancedData = {
          ...notification.data,
          // 🔥 백그라운드 알림 수신을 위해 data 필드에 notification 정보 포함
          title: notification.notification?.title || '',
          body: notification.notification?.body || '',
          message: notification.notification?.body || '', // 호환성을 위해 message 필드도 추가
          // 모든 data 값은 문자열이어야 함
          ...Object.keys(notification.data || {}).reduce((acc, key) => {
            acc[key] = String(notification.data[key]);
            return acc;
          }, {})
        };

        const messageData = {
          tokens: tokens.map(t => ({
            token: t.token,
            userId: t.userId,
            platform: t.platform || 'web',
            tokenType: t.tokenType || (t.platform === 'android' || t.platform === 'ios' ? 'native' : 'web'),
            tokenId: t.tokenId,
            locale: t.locale || 'en'
          })),
          notification: notification.notification,
          data: enhancedData, // 🔥 개선된 data 필드 사용
          android: {
            ...notification.android,
            priority: 'high', // 🔥 백그라운드 알림 수신을 위해 high 우선순위
            notification: {
              ...notification.android?.notification,
              priority: 'high', // 🔥 백그라운드 알림 수신을 위해 high 우선순위
              clickAction: 'FLUTTER_NOTIFICATION_CLICK',
              tag: `admin_notification_${Date.now()}`
            }
          },
          apns: {
            ...notification.apns,
            payload: {
              ...notification.apns?.payload,
              aps: {
                ...notification.apns?.payload?.aps,
                'content-available': 1, // 🔥 백그라운드 알림 수신을 위해 content-available 설정
                'mutable-content': 1
              }
            },
            headers: {
              ...notification.apns?.headers,
              'apns-priority': '10' // 🔥 백그라운드 알림 수신을 위해 높은 우선순위
            }
          },
          webpush: {
            notification: {
              title: notification.notification?.title || '',
              body: notification.notification?.body || '',
              icon: '/images/notifications/general-icon.png',
              badge: '/images/notifications/general-badge.png',
              requireInteraction: String(notification.data?.priority || '').toLowerCase() === 'high'
            },
            fcmOptions: {
              link: '/notifications'
            },
            headers: {
              Urgency: 'high',
              TTL: '3600'
            }
          }
        };

        console.log(`🚀 ${locale} 언어 알림을 Backend API로 직접 전송: ${tokens.length}개 토큰 대상`);
        console.log('🌐 API URL:', `${this.apiBaseUrl}/api/notifications/send-batch`);
        console.log('📤 전송 데이터 요약:', {
          tokenCount: tokens.length,
          title: messageData.notification?.title,
          body: messageData.notification?.body,
          hasAndroid: !!messageData.android,
          hasApns: !!messageData.apns
        });
        console.log('📤 전송 데이터 전체:', JSON.stringify(messageData, null, 2));
        
        try {
          console.log('📡 백엔드 API 호출 시작...');
          const fetchStartTime = Date.now();
          const response = await fetch(`${this.apiBaseUrl}/api/notifications/send-batch`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(messageData),
            mode: 'cors', // 🔥 CORS 모드 명시
            credentials: 'include' // 🔥 인증 정보 포함
          });
          const fetchDuration = Date.now() - fetchStartTime;
          console.log(`📡 백엔드 API 응답 수신 (${fetchDuration}ms):`, {
            status: response.status,
            statusText: response.statusText,
            ok: response.ok,
            headers: Object.fromEntries(response.headers.entries())
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ 백엔드 API 응답 오류:', {
              status: response.status,
              statusText: response.statusText,
              errorText: errorText
            });
            throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
          }

          const result = await response.json();
          console.log('✅ 백엔드 API 응답 성공:', JSON.stringify(result, null, 2));
          console.log(`✅ ${locale} 언어 알림 전송 완료:`, {
            success: result.success,
            successCount: result.successCount,
            failureCount: result.failureCount
          });
          
          if (result.success) {
            totalSuccess += result.successCount || tokens.length;
            totalFailure += result.failureCount || 0;
            results.push({
              locale: locale,
              tokenCount: tokens.length,
              successCount: result.successCount || tokens.length,
              failureCount: result.failureCount || 0,
              queued: false
            });
          } else {
            totalFailure += tokens.length;
            results.push({
              locale: locale,
              tokenCount: tokens.length,
              successCount: 0,
              failureCount: tokens.length,
              queued: false,
              error: result.error
            });
          }
        } catch (error) {
          console.error(`❌ ${locale} 언어 알림 전송 실패:`, {
            error: error.message,
            stack: error.stack,
            name: error.name,
            code: error.code,
            cause: error.cause
          });
          totalFailure += tokens.length;
          results.push({
            locale: locale,
            tokenCount: tokens.length,
            successCount: 0,
            failureCount: tokens.length,
            queued: false,
            error: error.message
          });
        }
      }

      console.log(`✅ 푸시알림 발송 완료: 성공 ${totalSuccess}건, 실패 ${totalFailure}건`);
      console.log('📊 상세 결과:', results);
      
      // 최종 성공 여부 판단: 1명 이상에게 성공적으로 전송되었는지
      const hasSuccessfulSends = totalSuccess > 0;
      
      const finalResult = {
        success: hasSuccessfulSends,
        sentCount: totalSuccess,
        failureCount: totalFailure,
        results: results,
        message: hasSuccessfulSends 
          ? `알림이 ${totalSuccess}명에게 성공적으로 발송되었습니다.` 
          : '알림 발송에 실패했습니다. FCM 토큰을 확인해주세요.',
        reason: hasSuccessfulSends ? null : '발송 가능한 활성 토큰이 없습니다.'
      };
      
      console.log('📊 최종 발송 결과:', {
        success: finalResult.success,
        totalSuccess,
        totalFailure,
        resultsCount: results.length
      });
      
      return finalResult;

    } catch (error) {
      console.error('❌ 푸시알림 발송 실패:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 공지사항 푸시알림 발송 (기존 호환성)
   * @param {Object} noticeData - 공지사항 데이터
   */
  async sendNoticeNotification(noticeData) {
    try {
      if (!this.isInitialized) {
        await this.initialize();
      }

      // Send notification only when notice is active
      if (noticeData.status !== 'active') {
        console.log('📝 공지사항이 비활성 상태이므로 알림을 발송하지 않습니다.');
        return { success: false, reason: 'inactive_notice' };
      }

      console.log('🔔 공지사항 푸시알림 발송 시작:', noticeData.title);

      // Query FCM tokens for all users
      const allTokens = await this.getAllUserFcmTokens();
      
      if (allTokens.length === 0) {
        console.warn('⚠️ 발송할 활성 토큰이 없습니다.');
        return { success: false, reason: 'no_tokens' };
      }

      // Set sender information
      const senderInfo = this.getSenderInfo(noticeData.sender);
      
      // Group tokens by language
      const tokensByLocale = this.groupTokensByLocale(allTokens);
      
      let totalSuccess = 0;
      let totalFailure = 0;
      const results = [];

      // Send notifications for each language
      for (const [locale, tokens] of tokensByLocale.entries()) {
        console.log(`📱 ${locale} 언어로 ${tokens.length}명에게 알림 발송`);
        
        const notification = this.createNotification(noticeData, senderInfo, locale);
        
        // Send through offline queuing system
        const messageData = {
          type: 'fcm',
          tokens: tokens.map(t => ({
            token: t.token,
            userId: t.userId,
            platform: t.platform || 'web',
            tokenType: t.tokenType || (t.platform === 'android' || t.platform === 'ios' ? 'native' : 'web'),
            tokenId: t.tokenId,
            locale: t.locale || 'en'
          })),
          notification: notification.notification,
          data: notification.data,
          android: notification.android,
          apns: notification.apns
        };

        // Add message to offline queue (automatic online/offline processing)
        await offlineMessageQueue.addMessage(messageData);
        
        console.log(`📝 ${locale} 언어 알림이 큐에 추가됨: ${tokens.length}개 토큰 대상`);
        
        totalSuccess += tokens.length;
        results.push({
          locale: locale,
          tokenCount: tokens.length,
          successCount: tokens.length,
          failureCount: 0,
          queued: true
        });
      }

      console.log(`✅ 공지사항 푸시알림 발송 완료: 성공 ${totalSuccess}건, 실패 ${totalFailure}건`);
      
      return {
        success: true,
        sentCount: totalSuccess,
        failureCount: totalFailure,
        results: results,
        message: 'Notice notification sent successfully'
      };

    } catch (error) {
      console.error('❌ 공지사항 푸시알림 발송 실패:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 발신자 정보 가져오기
   */
  getSenderInfo(sender) {
    const senderMap = {
      'admin': {
        name: 'Kairos Platform',
        nameKo: 'Kairos Platform',
        nameMy: 'Kairos Platform',
        nameEn: 'Kairos Platform',
        icon: '👨‍💼'
      },
      'myanmar_labor': {
        name: 'Ministry of Labour',
        nameKo: 'Ministry of Labour',
        nameMy: 'Ministry of Labour',
        nameEn: 'Ministry of Labour',
        icon: '🏛️'
      }
    };
    
    return senderMap[sender] || senderMap['admin'];
  }

  /**
   * 알림 메시지 생성 (개선된 버전)
   */
  createNotification(notificationData, senderInfo, locale) {
    const maxTitleLength = 50;
    const maxBodyLength = 200;
    
    // Settings by notification type
    const typeConfig = {
      normal: {
        prefix: '📢',
        color: '#1890ff',
        channelId: 'kairos_notifications',
        priority: 'normal',
        category: 'NOTICE',
        sound: 'notification_normal.mp3'
      },
      urgent: {
        prefix: '🚨',
        color: '#ff4d4f',
        channelId: 'kairos_urgent',
        priority: 'high',
        category: 'URGENT_NOTICE',
        sound: 'notification_urgent.mp3'
      },
      warning: {
        prefix: '⚠️',
        color: '#fa8c16',
        channelId: 'kairos_warning',
        priority: 'high',
        category: 'WARNING_NOTICE',
        sound: 'notification_warning.mp3'
      }
    };
    
    const config = typeConfig[notificationData.type] || typeConfig.normal;
    
    // Sender name by language
    let senderName;
    switch (locale) {
      case 'ko':
        senderName = senderInfo.nameKo;
        break;
      case 'my':
        senderName = senderInfo.nameMy;
        break;
      default:
        senderName = senderInfo.nameEn;
    }
    
    // Generate title (ID 제거)
    let cleanTitle = notificationData.title;
    // 더 강력한 ID 패턴 제거
    cleanTitle = cleanTitle
      .replace(/_[a-zA-Z0-9]+$/, '') // 끝에 오는 _문자열 제거
      .replace(/[0-9]+$/, '') // 끝에 오는 숫자 제거
      .replace(/[a-zA-Z0-9]{8,}$/, '') // 끝에 오는 8자 이상 문자열 제거 (Firestore ID 패턴)
      .replace(/\s+$/, '') // 끝에 오는 공백 제거
      .trim();
    let title = this.truncateText(cleanTitle, maxTitleLength - 10);
    title = `${config.prefix} ${title}`;
    
    // Determine data type and item ID first
    const dataType = notificationData.noticeId ? 'notice' : 'notification';
    const itemId = notificationData.noticeId || notificationData.id;
    
    // Generate body (실제 메시지 내용 사용)
    let body = this.truncateText(notificationData.message || notificationData.body || cleanTitle, maxBodyLength);
    
    // 🔍 Debug: Check generated notification data
    console.log('🔍 Admin에서 생성된 알림 데이터:');
    console.log('📱 원본 제목:', notificationData.title);
    console.log('📱 원본 메시지:', notificationData.message);
    console.log('📱 원본 body:', notificationData.body);
    console.log('📱 정리된 제목:', cleanTitle);
    console.log('📱 최종 title:', title);
    console.log('📝 최종 body:', body);
    console.log('👤 senderName:', senderName);
    console.log('🆔 itemId:', itemId);
    console.log('📊 dataType:', dataType);
    
    return {
      notification: {
        title: title,
        body: body
      },
      data: {
        type: dataType,
        id: String(itemId), // 간단한 ID 필드명
        priority: String(config.priority),
        sender: String(notificationData.sender || 'admin'),
        senderName: String(senderName),
        senderDisplayName: String(senderName),
        notification_type: String(notificationData.type || 'normal'), // ✅ 백엔드와 일치하도록 수정
        notificationType: String(notificationData.type || 'normal'), // 호환성을 위해 유지
        isUrgent: String(notificationData.type === 'urgent'),
        isWarning: String(notificationData.type === 'warning'),
        action: String(dataType === 'notice' ? 'view_notice' : 'view_notification'),
        timestamp: String(Date.now()),
        locale: String(locale),
        requireInteraction: String(notificationData.requireInteraction || false),
        userRequested: 'true' // FCM 메시지 표시를 위한 필수 필드
      },
      android: {
        priority: config.priority,
        notification: {
          priority: config.priority,
          sound: config.sound,
          channelId: config.channelId,
          icon: 'ic_notification',
          color: config.color,
          tag: `notification_${Date.now()}`,
          clickAction: 'FLUTTER_NOTIFICATION_CLICK'
        }
      },
      apns: {
        payload: {
          aps: {
            alert: {
              title: title,
              body: body
            },
            sound: config.sound || 'default',
            badge: 1,
            'content-available': 1,
            'mutable-content': 1,
            category: 'KAIROS_NOTIFICATION',
            'thread-id': `notification_${Date.now()}`
          }
        }
      }
    };
  }

  /**
   * 텍스트 길이 제한
   */
  truncateText(text, maxLength) {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  }

  /**
   * 토큰을 언어별로 그룹화
   */
  groupTokensByLocale(tokens) {
    const groups = new Map();
    
    tokens.forEach(token => {
      const locale = this.detectLocale(token.locale);
      if (!groups.has(locale)) {
        groups.set(locale, []);
      }
      groups.get(locale).push(token);
    });
    
    return groups;
  }

  /**
   * 언어 감지 및 정규화
   */
  detectLocale(locale) {
    if (!locale) return 'en';
    
    if (locale.startsWith('ko') || locale.includes('korean')) return 'ko';
    if (locale.startsWith('my') || locale.includes('myanmar') || locale.includes('burmese')) return 'my';
    return 'en';
  }

  /**
   * 배치 알림 발송
   */
  async sendBatchNotification(tokens, notification) {
    try {
      // 🔥 토큰 정보 객체 전체를 전송 (백엔드에서 platform, userId 등 추출 가능하도록)
      const tokenList = tokens.map(t => ({
        token: t.token,
        userId: t.userId,
        platform: t.platform || 'web',
        tokenType: t.tokenType || (t.platform === 'android' || t.platform === 'ios' ? 'native' : 'web'),
        tokenId: t.tokenId,
        locale: t.locale || 'en'
      }));
      
      // Backend API call
      const response = await fetch(`${this.apiBaseUrl}/api/notifications/send-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tokens: tokenList,
          notification: notification.notification,
          data: notification.data,
          android: notification.android,
          apns: notification.apns,
          webpush: {
            notification: {
              title: notification.notification?.title || '',
              body: notification.notification?.body || '',
              icon: '/images/notifications/general-icon.png',
              badge: '/images/notifications/general-badge.png',
              requireInteraction: String(notification.data?.priority || '').toLowerCase() === 'high'
            },
            fcmOptions: { link: '/notifications' },
            headers: { Urgency: 'high', TTL: '3600' }
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Batch notification failed: ${response.status}`);
      }

      const result = await response.json();
      
      // Backend 응답 처리
      console.log('📨 백엔드 응답:', {
        success: result.success,
        successCount: result.successCount,
        failureCount: result.failureCount
      });
      
      // 성공 여부 판단: successCount > 0이면 성공
      const hasSuccess = (result.successCount || 0) > 0;
      
      const finalResult = {
        success: hasSuccess,
        successCount: result.successCount || 0,
        failureCount: result.failureCount || 0,
        sentCount: result.successCount || 0,
        reason: hasSuccess ? null : (result.error || result.message || 'No devices received notification')
      };
      
      console.log('✅ 최종 결과:', finalResult);
      return finalResult;

    } catch (error) {
      console.error('❌ Batch notification failed:', error);
      return {
        success: false,
        successCount: 0,
        failureCount: tokens.length,
        sentCount: 0,
        reason: error.message || 'Unknown error'
      };
    }
  }

  /**
   * 공지사항 상태 변경 시 알림 처리
   */
  async handleNoticeStatusChange(noticeId, oldStatus, newStatus, noticeData) {
    try {
      // Send notification only when changed from inactive to active
      if (oldStatus === 'inactive' && newStatus === 'active') {
        console.log('📢 공지사항이 활성화되었습니다. 알림을 발송합니다.');
        return await this.sendNoticeNotification(noticeData);
      }
      
      // When changed from active to inactive
      if (oldStatus === 'active' && newStatus === 'inactive') {
        console.log('📝 공지사항이 비활성화되었습니다.');
        return { success: true, message: 'Notice deactivated' };
      }
      
      return { success: true, message: 'No notification needed' };
      
    } catch (error) {
      console.error('❌ 공지사항 상태 변경 알림 처리 실패:', error);
      return { success: false, error: error.message };
    }
  }
}

export const adminNotificationService = new AdminNotificationService();
